import React, {useEffect, useState} from 'react'
import styled from '@emotion/styled'
import {Color, ZIndex} from '../Styles'
import {MBContentBodyTypography, MBContentTitleTypography, MBDialogContentTypography} from '../Atoms/Typographies'
import {Spacer} from '../../../Common/Components/Spacer'
import {FlexContainer} from '../Atoms/Containers'
import {PIMStack} from '../Atoms/Layouts/Bases/PIMStack'
import {ConvenienceStoreInfo, ConvenienceStoreKey} from '../../Props/ContractInputInfo'
import {MBMemberSelect} from '../Atoms/MBMemberSelect'
import {convenienceStoreList} from '../Components/PaymentsRelations/InputContentBlocks/ConvenienceStoreInputComponent'

const StyledDiv = styled.div`
  display: block;
  background-color: ${Color.White};
  border-radius: 4px;
  padding: 16px 8px 8px 8px;
  z-index: ${ZIndex.Dialog};
  max-width: 80%;
`

export type MBBaseDialogProps = {
  testId?: string
  open: boolean
  title: string
  content: React.ReactNode
  buttons: React.ReactNode[]
}
export const MBBaseDialog: React.VFC<MBBaseDialogProps> = (props) => {
  useEffect(() => {
    if (props.open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'visible'
    }
  }, [props.open])

  return (
    <>
      {props.open &&
      <MBDialogBack data-testid={'mb-dialog-back'}>
        <FlexContainer
          alignItems={'center'}
          justifyContent={'center'}
          style={{height: '100%'}}
        >
          <StyledDiv data-testid={props.testId ?? 'mb-base-dialog'}>
            <PIMStack spacing={2}>
              <Spacer mx={'16px'}>
                <MBContentTitleTypography>{props.title}</MBContentTitleTypography>
              </Spacer>
              <Spacer mx={'16px'}>
                {props.content}
              </Spacer>
              <FlexContainer justifyContent={'flex-end'}>
                {props.buttons}
              </FlexContainer>
            </PIMStack>
          </StyledDiv>
        </FlexContainer>
      </MBDialogBack>
      }
    </>
  )
}

const MBDialogBack = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: ${Color.BlackOpacity};
  z-index: ${ZIndex.DialogBack}
`

const DialogButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  outline: 0;
  border: 0;
  cursor: pointer;
  border-radius: 4px;
  font-size: 1.3rem;
  color: ${Color.Blue200};
  min-width: 56px;
  padding: 6px 8px;

  &:hover {
    background-color: ${Color.BlueOpacity};
  }
`

export type MBDeleteCreditCardConfirmDialogProps = Pick<MBBaseDialogProps, 'open'> & {
  creditCardInfo: string
  onCancelButtonClick: () => void
  onOkButtonClick: () => Promise<void>
}
export const MBDeleteCreditCardConfirmDialog: React.VFC<MBDeleteCreditCardConfirmDialogProps> =
  (props) => {
    return (
      <MBBaseDialog
        testId={'mb-delete-credit-card-confirm-dialog'}
        open={props.open}
        title={'登録済みクレジットカード情報削除'}
        content={
          <MBDialogContentTypography>
            {`登録済みのクレジットカード情報\n${props.creditCardInfo}\nを削除しますか？`}
          </MBDialogContentTypography>
        }
        buttons={[
          <DialogButton
            key={'cancel-button'}
            onClick={(): void => props.onCancelButtonClick()}
          >
            キャンセル
          </DialogButton>,
          <DialogButton
            key={'ok-button'}
            onClick={(): Promise<void> => props.onOkButtonClick()}
          >
            OK
          </DialogButton>,
        ]}
      />
    )
  }

export type MBDeleteShippingAddressConfirmDialogProps = Pick<MBBaseDialogProps, 'open'> & {
  shippingAddressInfo: string
  onCancelButtonClick: () => void
  onOkButtonClick: () => Promise<void>
}
export const MBDeleteShippingAddressConfirmDialog:
  React.VFC<MBDeleteShippingAddressConfirmDialogProps> =
  ({
     open,
     shippingAddressInfo,
     onCancelButtonClick,
     onOkButtonClick,
   }) => {
    return (
      <MBBaseDialog
        testId={'mb-delete-shipping-address-confirm-dialog'}
        open={open}
        title={'商品配送先情報削除'}
        content={
          <MBDialogContentTypography>
            {`登録済みの商品配送先情報\n${shippingAddressInfo}\nを削除しますか？`}
          </MBDialogContentTypography>
        }
        buttons={[
          <DialogButton
            key={'cancel-button'}
            onClick={(): void => onCancelButtonClick()}
          >
            キャンセル
          </DialogButton>,
          <DialogButton
            key={'ok-button'}
            onClick={(): Promise<void> => onOkButtonClick()}
          >
            OK
          </DialogButton>,
        ]}
      />
    )
  }

export type MBCvsPaymentDialogProps = Pick<MBBaseDialogProps, 'open'> & {
  onCancelButtonClick: () => void
  onOkButtonClick: (cvsInfo: ConvenienceStoreInfo) => void
  defaultConvenienceStoreKey?: ConvenienceStoreKey
}
export const MBCvsPaymentDialog: React.VFC<MBCvsPaymentDialogProps> = (props) => {
  const [cvsInfo, setCvsInfo] = useState<ConvenienceStoreInfo>({
    convenienceStoreKey: props.defaultConvenienceStoreKey ?? '',
  })

  return <MBBaseDialog
    testId={'mb-cvs-payment-dialog'}
    open={props.open}
    title={'お支払いのコンビニを選択し、「確定」ボタンを押下してください。'}
    content={
      <PIMStack spacing={'18px'}>
        <MBContentBodyTypography>
          {'※「確定」ボタンを押下すると、選択したコンビニでのお支払い番号が発番されます。'}
        </MBContentBodyTypography>

        <FlexContainer alignItems={'center'} justifyContent={'center'} gap={'12px'}>
          <label htmlFor="cvs-payment-dialog-select-cvs">支払いコンビニ</label>
          <div style={{maxWidth: '300px'}}>
            <MBMemberSelect
              id="cvs-payment-dialog-select-cvs"
              name="convenienceStoreKey"
              fontSize={1.3}
              hasError={false}
              onChange={(e): void => setCvsInfo({
                convenienceStoreKey: e.target.value as ConvenienceStoreKey,
              })}
              value={cvsInfo.convenienceStoreKey}
            >
              <option value="" hidden disabled>選択してください</option>
              {convenienceStoreList.map((item) => (
                <option key={item.convenienceStoreKey} value={item.convenienceStoreKey}>
                  {item.name}
                </option>
              ))}
            </MBMemberSelect>
          </div>
        </FlexContainer>
      </PIMStack>
    }
    buttons={[
      <DialogButton key={'cancel-button'} onClick={(): void => props.onCancelButtonClick()}>
        キャンセル
      </DialogButton>,
      <DialogButton key={'ok-button'} onClick={(): void => props.onOkButtonClick(cvsInfo)}>
        確定
      </DialogButton>,
    ]}
  />
}
